import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, inputClass, Avatar, asList } from './dashboardShared';

// The dashboard's Partners tab (bot owner): each partner server gets ONE Whatnot, Twitch and YouTube account, set here
// and fixed. The server's managers then pick where each posts, and switch it on, from their Partner Dashboard.

export const PARTNER_FEEDS = [ // in this order everywhere: Whatnot, Twitch, YouTube
  { id: 'whatnot', label: 'Whatnot', color: '#fbe510', text: '#000', placeholder: 'Whatnot username or profile link', what: 'goes live on Whatnot' },
  { id: 'twitch', label: 'Twitch', color: '#9146ff', placeholder: 'Twitch username', what: 'goes live on Twitch' },
  { id: 'youtube', label: 'YouTube', color: '#ff0000', placeholder: 'Channel link or @handle', what: 'posts a new YouTube video or goes live' },
];

// Firebase drops empty lists and empty values: put every server's three feeds back in shape
export const tidyFeeds = (feeds) => Object.fromEntries(PARTNER_FEEDS.map(f => {
  const feed = feeds?.[f.id] || {};
  return [f.id, { account: feed.account || null, channel: feed.channel || null, channelName: feed.channelName || null,
    role: feed.role || null, products: feed.products || null, on: !!feed.on, running: feed.running !== false }];
}));

export default function PartnersTab({ ask, setNote, failed }) {
  const [servers, setServers] = useState(null);
  const [people, setPeople] = useState([]);   // who's a partner
  const [drafts, setDrafts] = useState({}); // "<server>-<kind>" -> what's being typed
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState(null);
  const [open, setOpen] = useState({}); // server id -> its three accounts showing (each starts folded up)
  const searchId = useRef(0);

  const take = (answer) => {
    if (failed(answer)) return false;
    setServers(asList(answer.data?.servers).map(s => ({ ...s, feeds: tidyFeeds(s.feeds) })));
    setPeople(asList(answer.data?.people).map(p => ({ ...p, servers: asList(p.servers) })));
    if (answer.data?.message) setNote({ text: answer.data.message, ok: true });
    return true;
  };
  const load = useCallback(async () => take(await ask('partners.list')), [ask]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  // Find CHΞΞKSCORD members as you type (a moment after you stop)
  useEffect(() => {
    if (query.trim().length < 2) { setFound(null); return undefined; }
    const id = ++searchId.current;
    const timer = setTimeout(async () => {
      const answer = await ask('partners.find', { query });
      if (id === searchId.current && answer.ok) setFound(asList(answer.data?.members));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const person = async (verb, member) => {
    const key = `${verb}-${member}`;
    if (verb === 'remove' && confirm !== key) { setConfirm(key); return; }
    setConfirm(''); setBusy(key); setNote(null);
    if (take(await ask(`partners.${verb}`, { member }))) {
      setFound(f => (f ? f.map(m => (m.id === member ? { ...m, partner: verb === 'add' } : m)) : f));
    }
    setBusy('');
  };

  const save = async (server, kind, value) => {
    const key = `${server}-${kind}`;
    setBusy(key); setNote(null);
    if (take(await ask('partners.set', { guild: server, kind, value }))) setDrafts(d => ({ ...d, [key]: undefined }));
    setBusy('');
  };

  if (!servers) return <Card><p className="text-zinc-500 text-xs">Loading…</p></Card>;

  return (
    <div className="space-y-4">
      <Card title={`Partners · ${people.length}`}>
        <p className="text-zinc-400 text-xs mb-2">
          Partners are people you choose. Only they see the Partner Dashboard on cheeks.uk, where they can add TOGΞ to
          their server (you still approve it, in Gatekeep or from your DM) and pick where it posts.
        </p>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a CHΞΞKSCORD member by name or ID" className={inputClass} />
        {found && (
          <ul className="mt-2 divide-y divide-zinc-800">
            {found.length === 0 && <li className="py-2 text-zinc-500 text-xs">Nobody in CHΞΞKSCORD matches that.</li>}
            {found.map(m => (
              <li key={m.id} className="flex items-center gap-2 py-2">
                <Avatar src={m.avatar} name={m.name} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{m.name}{m.partner && <span className="ml-1.5">🤝</span>}</p>
                  <p className="text-zinc-500 text-[11px] truncate">@{m.username}</p>
                </div>
                {m.partner
                  ? <span className="text-zinc-500 text-[11px]">Already a partner</span>
                  : <Button small tone="primary" disabled={!!busy} onClick={() => person('add', m.id)}>{busy === `add-${m.id}` ? '…' : 'Make partner'}</Button>}
              </li>
            ))}
          </ul>
        )}
        {people.length > 0 && (
          <ul className="mt-3 divide-y divide-zinc-800 border-t border-zinc-800">
            {people.map(p => (
              <li key={p.id} className="flex items-center gap-2 py-2">
                <Avatar src={p.avatar} name={p.name} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{p.name}{!p.here && <span className="text-zinc-500 font-normal"> (left CHΞΞKSCORD)</span>}</p>
                  <p className="text-zinc-500 text-[11px] truncate">
                    {p.servers.length ? `Servers: ${p.servers.join(', ')}` : "Hasn't added TOGΞ to a server yet"}
                  </p>
                </div>
                <Button small tone={confirm === `remove-${p.id}` ? 'danger' : 'ghost'} disabled={!!busy} onClick={() => person('remove', p.id)}>
                  {busy === `remove-${p.id}` ? '…' : confirm === `remove-${p.id}` ? 'Sure?' : 'Remove'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <p className="text-zinc-400 text-xs">
          Partner servers are other servers TOGΞ is in and approved for. Set each one's Whatnot, Twitch and YouTube account
          here: they can't change them. The partner then chooses the channel for each, and switches it on, from their Partner
          Dashboard. They only get going-live, new product and new video posts: no commands, boards or anything else.
        </p>
      </Card>

      {servers.length === 0 && (
        <Card><p className="text-zinc-500 text-xs">TOGΞ isn't in any partner servers yet. Once a partner adds it to their server and you approve it, it shows here.</p></Card>
      )}

      {servers.map(s => (
        <Card key={s.id}>
          {/* Folded up: the server, and a pill for each account (dim: not set · outlined: set, off · filled: on) */}
          <button type="button" onClick={() => setOpen(o => ({ ...o, [s.id]: !o[s.id] }))} aria-expanded={!!open[s.id]}
            className="w-full flex items-center gap-2 text-left">
            <Avatar src={s.icon} name={s.name} size={32} />
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold truncate">{s.name}</p>
              <p className="text-zinc-500 text-[11px] truncate">{(s.members || 0).toLocaleString()} members · owner {s.owner}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {PARTNER_FEEDS.map(f => {
                const feed = s.feeds[f.id];
                return (
                  <span key={f.id} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border"
                    title={`${f.label}: ${feed.on ? `on · #${feed.channelName || 'a deleted channel'}` : feed.account ? 'set, off' : 'not set'}`}
                    style={feed.on ? { background: f.color, color: f.text || 'white', borderColor: f.color }
                      : feed.account ? { color: f.color, borderColor: f.color }
                        : { color: '#52525b', borderColor: '#3f3f46' }}>
                    {f.label}
                  </span>
                );
              })}
            </div>
            <span className={`text-zinc-500 text-xs transition-transform ${open[s.id] ? 'rotate-180' : ''}`} aria-hidden="true">▾</span>
          </button>
          {open[s.id] && <div className="space-y-2 mt-3">
            {PARTNER_FEEDS.map(f => {
              const feed = s.feeds[f.id];
              const key = `${s.id}-${f.id}`;
              const typed = drafts[key];
              return (
                <div key={f.id} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: f.color, color: f.text || 'white' }}>{f.label}</span>
                    {feed.account
                      ? <a href={feed.account.url} target="_blank" rel="noreferrer" className="text-white text-xs font-semibold hover:underline">{feed.account.name}</a>
                      : <span className="text-zinc-500 text-xs">Not set</span>}
                    <span className={`ml-auto text-[10px] font-semibold ${feed.on ? 'text-green-400' : 'text-zinc-500'}`}>
                      {feed.on ? `On · #${feed.channelName || 'a deleted channel'}` : feed.account ? 'Off (their choice)' : ''}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input value={typed ?? ''} onChange={e => setDrafts(d => ({ ...d, [key]: e.target.value }))}
                      placeholder={feed.account ? `Change: ${f.placeholder}` : f.placeholder} className={inputClass}
                      onKeyDown={e => { if (e.key === 'Enter' && typed && !busy) save(s.id, f.id, typed); }} />
                    <Button small tone="primary" disabled={!typed || !!busy} onClick={() => save(s.id, f.id, typed)}>{busy === key ? '…' : 'Set'}</Button>
                    {feed.account && <Button small tone="ghost" disabled={!!busy} onClick={() => save(s.id, f.id, '')}>Clear</Button>}
                  </div>
                </div>
              );
            })}
          </div>}
        </Card>
      ))}
    </div>
  );
}
